import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import toast from "react-hot-toast";
import {
  FaPlus,
  FaSearch,
  FaPen,
  FaTrash,
  FaUsers,
  FaCalendarAlt,
  FaCheck,
} from "react-icons/fa";
import styles from "./EmployeesList.module.css";
import { useNavigate } from "react-router-dom";
import { DeleteModal } from "../Fields/FieldDetails.js";

// --- HELPER FUNCTIONS ---
const formatDate = (dateString, locale) => {
  if (!dateString) return "—";
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(dateString).toLocaleDateString(locale, options);
};

// const getInitials = (name = "") => {
//   return name
//     .split(" ")
//     .map((n) => n[0])
//     .join("")
//     .substring(0, 2);
// };

// --- SUB-COMPONENTS (Employees Tab) ---

const EmployeesTable = ({
  employees,
  userData,
  t,
  i18n,
  onEdit,
  onDelete,
  onRowClick,
}) => (
  <div className={styles.tableContainer}>
    <table>
      <thead>
        <tr>
          <th>{t("Employee")}</th>
          <th>{t("Email")}</th>
          <th>{t("Job")}</th>
          <th>{t("Date of Employment")}</th>
          <th>{t("Last Visit")}</th> {/* Add New Column Header */}
          <th>{t("Actions")}</th>
        </tr>
      </thead>
      <tbody>
        {employees
          .filter((emp) => emp.Employee)
          .map(({ Employee, User, Role }) => (
            <tr
              key={Employee.id}
              onClick={() => onRowClick(Employee.id)}
              className={styles.clickableRow}
            >
              <td>
                <div className={styles.employeeNameCell}>
                  <span className={styles.employeeName}>
                    {Employee.username}
                  </span>
                  {userData?.Employee?.id === Employee.id && (
                    <span className={styles.youBadge}>{t("You")}</span>
                  )}
                </div>
              </td>
              <td>{User?.email || "—"}</td>
              <td>
                {(i18n.language === "ar" ? Role?.nameAr : Role?.nameEn) || "—"}
              </td>
              <td>
                {formatDate(
                  Employee.createTs,
                  i18n.language === "ar" ? "ar-EG" : "en-US"
                )}
              </td>
              {/* Add New Column Data */}
              <td>
                {formatDate(
                  Employee.lastVisit,
                  i18n.language === "ar" ? "ar-EG" : "en-US"
                )}
              </td>
              <td>
                <div className={styles.actions}>
                  <button
                    className={styles.actionButton}
                    aria-label={t("Edit")}
                    onClick={() => onEdit(Employee.id)}
                  >
                    <FaPen />
                  </button>
                  <button
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                    aria-label={t("Delete")}
                    onClick={() => onDelete(Employee)}
                  >
                    <FaTrash />
                  </button>
                </div>
              </td>
            </tr>
          ))}
      </tbody>
    </table>
  </div>
);

// --- UPDATED SUB-COMPONENTS (Laborers Tab) ---

// const LaborerFinanceRow = ({
//   laborer,
//   t,
//   isEditingLaborerId,
//   onEdit,
//   onSave,
// }) => {
//   const [workDays, setWorkDays] = useState(laborer.totalWorkDays || 0);
//   const isEditing = isEditingLaborerId === laborer.employeeId;

//   return (
//     <div className={styles.laborerRow}>
//       <div className={`${styles.laborerCell} ${styles.userCell}`}>
//         <div className={styles.avatar}>{getInitials(laborer.employeeName)}</div>
//         <span>{laborer.employeeName}</span>
//       </div>
//       <div className={styles.laborerCell}>
//         {isEditing ? (
//           <div className={styles.editInputContainer}>
//             <input
//               type="number"
//               value={workDays}
//               onChange={(e) => setWorkDays(e.target.value)}
//               className={styles.workDaysInput}
//               autoFocus
//             />
//             <button
//               onClick={() => onSave(laborer.employeeId, workDays)}
//               className={styles.saveButton}
//             >
//               <FaCheck />
//             </button>
//           </div>
//         ) : (
//           <>
//             <FaPen
//               className={styles.editIcon}
//               onClick={() => onEdit(laborer.employeeId)}
//             />
//             {t("day_count", { count: laborer.totalWorkDays || 0 })}
//           </>
//         )}
//       </div>
//       <div className={styles.laborerCell}>
//         <span className={styles.wageTypeBadge}>
//           {laborer.rateType || "N/A"}
//         </span>
//       </div>
//       <div className={styles.laborerCell}>
//         {laborer.rate?.toFixed(1) || "0.0"}
//       </div>
//       <div className={styles.laborerCell}>
//         {laborer.totalCost?.toFixed(1) || "0.0"}
//       </div>
//       <div className={styles.laborerCell}>
//         {laborer.amountPaid?.toFixed(1) || "0.0"}
//       </div>
//       <div className={styles.laborerCell}>
//         {laborer.remainingBalance?.toFixed(1) || "0.0"}
//       </div>
//     </div>
//   );
// };
const LaborerFinanceRow = ({
  laborer,
  t,
  isEditingLaborerId,
  onEdit,
  onSave,
  onRowClick,
}) => {
  // For hourly workers, totalWorkDays actually contains hours
  const [workDuration, setWorkDuration] = useState(laborer.totalWorkDays || 0);
  const isEditing = isEditingLaborerId === laborer.employeeId;

  // Determine the correct translation key based on rateType
  // Note: For hourly workers, totalWorkDays contains hours
  const durationText =
    laborer.rateType === "hourly"
      ? t("hour_count", { count: laborer.totalWorkDays || 0 })
      : t("day_count", { count: laborer.totalWorkDays || 0 });

  return (
    <div className={`${styles.laborerRow} ${styles.clickableRow}`}>
      <div
        className={`${styles.laborerCell} ${styles.userCell}`}
        onClick={() => onRowClick(laborer.employeeId)}
        style={{ cursor: "pointer" }}
      >
        {/* <div className={styles.avatar}>{getInitials(laborer.employeeName)}</div> */}
        <span>{laborer.employeeName}</span>
      </div>
      <div className={styles.laborerCell}>
        {isEditing ? (
          <div className={styles.editInputContainer}>
            <input
              type="number"
              value={workDuration}
              onChange={(e) => setWorkDuration(e.target.value)}
              className={styles.workDaysInput}
              autoFocus
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onSave(laborer.employeeId, workDuration);
              }}
              className={styles.saveButton}
            >
              <FaCheck />
            </button>
          </div>
        ) : (
          <>
            <FaPen
              className={styles.editIcon}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onEdit(laborer.employeeId);
              }}
              style={{ cursor: "pointer", position: "relative", zIndex: 10 }}
            />
            {durationText}
          </>
        )}
      </div>
      <div
        className={styles.laborerCell}
        onClick={() => onRowClick(laborer.employeeId)}
        style={{ cursor: "pointer" }}
      >
        <span className={styles.wageTypeBadge}>
          {laborer.rateType || "N/A"}
        </span>
      </div>
      <div
        className={styles.laborerCell}
        onClick={() => onRowClick(laborer.employeeId)}
        style={{ cursor: "pointer" }}
      >
        {laborer.rate?.toFixed(1) || "0.0"}
      </div>
      <div
        className={styles.laborerCell}
        onClick={() => onRowClick(laborer.employeeId)}
        style={{ cursor: "pointer" }}
      >
        {laborer.totalCost?.toFixed(1) || "0.0"}
      </div>
      <div
        className={styles.laborerCell}
        onClick={() => onRowClick(laborer.employeeId)}
        style={{ cursor: "pointer" }}
      >
        {laborer.amountPaid?.toFixed(1) || "0.0"}
      </div>
      <div
        className={styles.laborerCell}
        onClick={() => onRowClick(laborer.employeeId)}
        style={{ cursor: "pointer" }}
      >
        {laborer.remainingBalance?.toFixed(1) || "0.0"}
      </div>
    </div>
  );
};

const LaborersFinanceList = (props) => (
  <div className={styles.financeListContainer}>
    <div className={`${styles.laborerRow} ${styles.laborerHeaderRow}`}>
      <div className={styles.laborerCell}>{props.t("User")}</div>
      <div className={styles.laborerCell}>{props.t("Work Duration")}</div>
      <div className={styles.laborerCell}>{props.t("Wage Type")}</div>
      <div className={styles.laborerCell}>{props.t("Wage")}</div>
      <div className={styles.laborerCell}>{props.t("Total Cost")}</div>
      <div className={styles.laborerCell}>{props.t("Paid")}</div>
      <div className={styles.laborerCell}>{props.t("Remaining")}</div>
    </div>
    {props.laborers.map((laborer) => (
      <LaborerFinanceRow
        key={laborer.employeeId}
        laborer={laborer}
        {...props}
      />
    ))}
  </div>
);

const LaborersGroupedBySupervisor = (props) => (
  <div className={styles.financeListContainer}>
    <div className={`${styles.laborerRow} ${styles.laborerHeaderRow}`}>
      <div className={styles.laborerCell}>{props.t("User")}</div>
      <div className={styles.laborerCell}>{props.t("Work Duration")}</div>
      <div className={styles.laborerCell}>{props.t("Wage Type")}</div>
      <div className={styles.laborerCell}>{props.t("Wage")}</div>
      <div className={styles.laborerCell}>{props.t("Total Cost")}</div>
      <div className={styles.laborerCell}>{props.t("Paid")}</div>
      <div className={styles.laborerCell}>{props.t("Remaining")}</div>
    </div>
    {Object.keys(props.groupedData).map((supervisorName) => (
      <div key={supervisorName} className={styles.supervisorGroup}>
        <h3 className={styles.supervisorHeader}>
          {supervisorName === "Unassigned"
            ? props.t("Without Supervisor")
            : supervisorName}
          <span>
            {props.t("laborer_count_badge", {
              count: props.groupedData[supervisorName].length,
            })}
          </span>
        </h3>
        {props.groupedData[supervisorName].map((laborer) => (
          <LaborerFinanceRow
            key={laborer.employeeId}
            laborer={laborer}
            {...props}
          />
        ))}
      </div>
    ))}
  </div>
);

const LaborerControls = ({ viewBySupervisor, onViewChange, t }) => (
  <div className={styles.laborControlsContainer}>
    <div className={styles.toggleContainer}>
      <span>{t("By Supervisor")}</span>
      <label className={styles.switch}>
        <input
          type="checkbox"
          checked={viewBySupervisor}
          onChange={(e) => onViewChange(e.target.checked)}
        />
        <span className={styles.slider}></span>
      </label>
    </div>
  </div>
);

// --- MAIN COMPONENT ---
const EmployeesList = () => {
  const { t, i18n } = useTranslation();
  const { userData, token } = useContext(UserContext);

  const [activeTab, setActiveTab] = useState("employees");
  const [allEmployees, setAllEmployees] = useState([]);
  const [displayEmployees, setDisplayEmployees] = useState([]);
  const [laborersFinanceData, setLaborersFinanceData] = useState([]);
  const [displayLaborers, setDisplayLaborers] = useState([]);
  const [groupedLaborers, setGroupedLaborers] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [viewBySupervisor, setViewBySupervisor] = useState(false);
  const [editingLaborerId, setEditingLaborerId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);

  // Data fetching logic
  useEffect(() => {
    const cancelToken = axios.CancelToken.source();
    const fetchData = async () => {
      if (!userData?.Tenant?.id || !token) return;
      setLoading(true);

      if (activeTab === "employees") {
        setLaborersFinanceData([]);
        try {
          const response = await axios.get(
            `/api/emp/findByTenantId/${userData.Tenant.id}`,
            {
              headers: { Authorization: token },
              cancelToken: cancelToken.token,
            }
          );
          console.log("employees before filtring", response.data.items);
          const employees = response.data.items.filter(
            (employee) =>
              employee.Employee.accountType?.toLowerCase() === "permanent"
          );
          console.log("employees after filtring", employees);
          setAllEmployees(employees || []);
        } catch (error) {
          if (!axios.isCancel(error))
            toast.error(t("Could not load employees."));
        } finally {
          setLoading(false);
        }
      } else {
        // Laborers Tab
        setAllEmployees([]);
        try {
          const [year, month] = selectedMonth.split("-").map(Number);
          const firstDay = new Date(year, month - 1, 1);
          const lastDay = new Date(year, month, 0);
          const fromTimestamp = firstDay.setHours(0, 0, 0, 0);
          const toTimestamp = lastDay.setHours(23, 59, 59, 999);
          const farmId = userData.Farm?.id || 1;

          // --- CORRECTED LOGIC: Fetch both datasets in parallel ---
          const financePromise = axios.get(
            `/api/emp/empFinance/${farmId}/${userData.Tenant.id}?from=${fromTimestamp}&to=${toTimestamp}`,
            {
              headers: { Authorization: token },
              cancelToken: cancelToken.token,
            }
          );
          const employeesPromise = axios.get(
            `/api/emp/findByTenantId/${userData.Tenant.id}`,
            {
              headers: { Authorization: token },
              cancelToken: cancelToken.token,
            }
          );

          const [financeResponse, employeesResponse] = await Promise.all([
            financePromise,
            employeesPromise,
          ]);

          const financeData = financeResponse.data.items || [];
          const allEmployeesData = employeesResponse.data.items || [];

          // --- CORRECTED LOGIC: Create a lookup map for SUPERVISOR NAMES ---
          const supervisorMap = new Map();
          allEmployeesData.forEach((emp) => {
            // Find users whose role is 'Supervisor' and add them to the map
            if (emp.Role?.nameEn === "Supervisor") {
              supervisorMap.set(emp.Employee.id, emp.Employee.username);
            }
          });

          // Create a lookup map for each employee's supervisorId
          const employeeSupervisorIdMap = new Map();
          allEmployeesData.forEach((emp) => {
            if (emp.Employee) {
              employeeSupervisorIdMap.set(
                emp.Employee.id,
                emp.Employee.supervisorId
              );
            }
          });

          // --- CORRECTED LOGIC: Merge the two datasets ---
          const mergedLaborers = financeData.map((laborer) => {
            // 1. Find the laborer's supervisorId from the employee list
            const supervisorId = employeeSupervisorIdMap.get(
              laborer.employeeId
            );
            // 2. Find the supervisor's name using their ID
            const supervisorName = supervisorMap.get(supervisorId);

            return {
              ...laborer,
              // Add the found supervisorName to the object
              supervisorName: supervisorName || null,
            };
          });

          setLaborersFinanceData(mergedLaborers);
        } catch (error) {
          if (!axios.isCancel(error))
            toast.error(t("Could not load laborers finance data."));
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => cancelToken.cancel();
  }, [userData, token, t, activeTab, selectedMonth]);

  // Client-side filtering and grouping
  useEffect(() => {
    if (activeTab === "employees") {
      let filtered = allEmployees;
      if (searchTerm) {
        filtered = allEmployees.filter(
          (emp) =>
            emp.Employee?.username
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            emp.User?.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      setDisplayEmployees(filtered);
    } else {
      let filtered = laborersFinanceData;
      console.log("filter data", filtered);
      if (searchTerm) {
        filtered = laborersFinanceData.filter((laborer) =>
          laborer.employeeName?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      setDisplayLaborers(filtered);
      const grouped = filtered.reduce((acc, curr) => {
        const supervisorName = curr.supervisorName || "Unassigned";
        if (!acc[supervisorName]) {
          acc[supervisorName] = [];
        }
        acc[supervisorName].push(curr);
        return acc;
      }, {});

      console.log("grouped data", grouped);
      const sortedGroupKeys = Object.keys(grouped).sort((a, b) => {
        if (a === "Unassigned") return 1;
        if (b === "Unassigned") return -1;
        return a.localeCompare(b);
      });

      const sortedGroupedLaborers = {};
      sortedGroupKeys.forEach((key) => {
        sortedGroupedLaborers[key] = grouped[key];
      });

      setGroupedLaborers(sortedGroupedLaborers);
    }
  }, [searchTerm, allEmployees, laborersFinanceData, activeTab]);

  const handleEditEmployee = (employeeId) => {
    navigate(`/employees/${employeeId}/edit`);
  };

  const handleRowClick = (employeeId) => {
    // 7. Create handler function
    navigate(`/employees/${employeeId}`);
  };

  // 1. This function will be called when the trash icon is clicked
  const openDeleteModal = (employee) => {
    setEmployeeToDelete(employee); // Store the entire employee object
    setIsDeleteModalOpen(true);
  };

  // 2. This function closes the modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setEmployeeToDelete(null);
  };

  // 3. This function executes the deletion after confirmation
  const confirmDelete = async () => {
    if (!employeeToDelete) return;

    try {
      await axios.delete(`/api/emp/${employeeToDelete.id}`, {
        // [cite: 92, 93]
        headers: { Authorization: token },
      });

      toast.success(t("Employee deleted successfully!"));

      // Update state to remove the employee from the list
      setAllEmployees((prevEmployees) =>
        prevEmployees.filter((emp) => emp.Employee.id !== employeeToDelete.id)
      );
    } catch (error) {
      toast.error(t("Failed to delete employee."));
      console.error("Delete Error:", error);
    } finally {
      // Close the modal whether it succeeds or fails
      closeDeleteModal();
    }
  };

  const handleSaveWorkDays = async (employeeId, newWorkDays) => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const farmId = userData.Farm?.id || 1;

    const payload = {
      employeeId: employeeId,
      year: year,
      month: month,
      workDays: parseInt(newWorkDays, 10),
    };

    try {
      await axios.post(`/api/workLog`, payload, {
        headers: { Authorization: token },
      });
      toast.success(t("Work days updated successfully!"));
      setEditingLaborerId(null);
      // Re-fetch data to see updates in costs and balances
      const fromTimestamp = new Date(year, month - 1, 1).setHours(0, 0, 0, 0);
      const toTimestamp = new Date(year, month, 0).setHours(23, 59, 59, 999);
      const response = await axios.get(
        `/api/emp/empFinance/${farmId}/${userData.Tenant.id}?from=${fromTimestamp}&to=${toTimestamp}`,
        { headers: { Authorization: token } }
      );
      setLaborersFinanceData(response.data.items || []);
    } catch (error) {
      toast.error(t("Failed to update work days."));
    }
  };

  const renderContent = () => {
    if (loading)
      return <div className={styles.messageCell}>{t("Loading...")}</div>;

    if (activeTab === "employees") {
      if (displayEmployees.length === 0)
        return (
          <div className={styles.messageCell}>{t("No employees found.")}</div>
        );
      return (
        <EmployeesTable
          employees={displayEmployees}
          userData={userData}
          t={t}
          i18n={i18n}
          onEdit={handleEditEmployee}
          onDelete={openDeleteModal}
          onRowClick={handleRowClick}
        />
      );
    }

    if (activeTab === "laborers") {
      if (displayLaborers.length === 0)
        return (
          <div className={styles.messageCell}>
            {t("No laborers found for this month.")}
          </div>
        );
      const commonProps = {
        t: t,
        onEdit: setEditingLaborerId,
        isEditingLaborerId: editingLaborerId,
        onSave: handleSaveWorkDays,
        onRowClick: handleRowClick,
      };

      return viewBySupervisor ? (
        <LaborersGroupedBySupervisor
          groupedData={groupedLaborers}
          {...commonProps}
        />
      ) : (
        <LaborersFinanceList laborers={displayLaborers} {...commonProps} />
      );
    }
    return null;
  };

  const isLaborersTab = activeTab === "laborers";
  const count = isLaborersTab
    ? laborersFinanceData.length
    : allEmployees.length;

  return (
    <div className={styles.container}>
      <div className={styles.mainHeader}>
        <h1 className={styles.mainTitle}>{t("Employees")}</h1>
        <button
          className={styles.salariesButton}
          onClick={() => navigate("/salaries-summary")}
        >
          {t("Salaries Summary")}
        </button>
      </div>
      <div className={styles.tabs}>
        <button
          className={`${styles.tabButton} ${
            activeTab === "employees" ? styles.active : ""
          }`}
          onClick={() => setActiveTab("employees")}
        >
          {t("Employees")}
        </button>
        <button
          className={`${styles.tabButton} ${
            activeTab === "laborers" ? styles.active : ""
          }`}
          onClick={() => setActiveTab("laborers")}
        >
          {t("Laborers")}
        </button>
      </div>
      {/* <div
        className={isLaborersTab ? styles.laborerSubHeader : styles.subHeader}
      >
        {isLaborersTab ? (
          <>
            <div className={styles.subHeaderLeft}>
              <div className={styles.subHeaderTitle}>
                <FaUsers />
                <h2>{t("Laborers")}</h2>
              </div>
              <div className={styles.laborerCountDisplay}>
                {t("laborer_count", { count })}
              </div>
            </div>
            <LaborerControls
              viewBySupervisor={viewBySupervisor}
              onViewChange={setViewBySupervisor}
              t={t}
            />
          </>
        ) : (
          <div className={styles.subHeaderLeft}>
            <div className={styles.subHeaderTitle}>
              <FaUsers />
              <h2>{t("Farm Employees")}</h2>
            </div>
            <p className={styles.employeeCount}>
              {t("employees_count", { count })}
            </p>
          </div>
        )}
        <div className={styles.subHeaderRight}>
          {isLaborersTab && (
            <button className={styles.addButton}>
              <FaPlus />
              <span>{t("Add Laborer")}</span>
            </button>
          )}
        </div>
        <div className={styles.subHeaderRight}>
          {!isLaborersTab && (
            <button className={styles.addButton}>
              <FaPlus />
              <span>{t("Add Employee")}</span>
            </button>
          )}
        </div>
      </div>

      {isLaborersTab && (
        <div className={styles.laborerSearchAndDate}>
          <div className={styles.searchContainer}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder={t("Search...")}
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className={styles.monthPickerContainer}>
            <FaCalendarAlt />
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          </div>
        </div>
      )}

      {!isLaborersTab && (
        <div className={styles.searchContainerAlone}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder={t("Search...")}
            className={styles.searchInput}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )} */}
      {/* --- REVISED HEADER & CONTROLS SECTION --- */}
      <div className={styles.subHeader}>
        <div className={styles.subHeaderLeft}>
          <div className={styles.subHeaderTitle}>
            <FaUsers />
            <h2>{isLaborersTab ? t("Laborers") : t("Farm Employees")}</h2>
          </div>
          <p className={styles.employeeCount}>
            {t(isLaborersTab ? "laborer_count" : "employees_count", { count })}
          </p>
        </div>
        <div className={styles.subHeaderRight}>
          <button
            className={styles.addButton}
            onClick={() => {
              navigate(isLaborersTab ? "/laborers/new" : "/employees/new");
            }}
          >
            <FaPlus />
            <span>{isLaborersTab ? t("Add Laborer") : t("Add Employee")}</span>
          </button>
        </div>
      </div>

      <div className={styles.controlsAndSearch}>
        {/* Search Bar is always visible */}
        <div className={styles.searchContainer}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder={t("Search...")}
            className={styles.searchInput}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Laborer-specific controls appear only on that tab */}
        {isLaborersTab && (
          <div className={styles.laborerControlsWrapper}>
            <div className={styles.monthPickerContainer}>
              <FaCalendarAlt />
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
            </div>
            <LaborerControls
              viewBySupervisor={viewBySupervisor}
              onViewChange={setViewBySupervisor}
              t={t}
            />
          </div>
        )}
      </div>

      {renderContent()}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        itemName={employeeToDelete?.username || ""}
      />
    </div>
  );
};

export default EmployeesList;
